import { Aurelia, PLATFORM } from 'aurelia-framework';
import { I18N, TCustomAttribute } from 'aurelia-i18n';
import { AppRouter } from 'aurelia-router';
import {
	FluentRuleCustomizer,
	ValidationMessageProvider,
	validationMessages,
} from 'aurelia-validation';
import Bluebird from 'bluebird';
import Backend from 'i18next-xhr-backend';
import moment from 'moment';

import 'jquery';

// NOTE: Jake: 2019-04-15 - WEBUI-1749
// This makes $.param encoding work for Spring Boot with jQuery.
import 'vendor/jquery.spring-friendly.ts';

// NOTE: Jake: 2019-04-15 - WEBUI-1749
// Bootstrap is used for tooltips and for the multiselect field.
import 'bootstrap';
import 'bootstrap-select';

// NOTE: Jake: 2019-12-03 - WEBUI-2752
// Must be imported after 'bootstrap' so it overrides the original Bootstrap
// implementation of tooltip.
import '~/vendor/bootstrap-tooltip';

// import 'highlight.js' would import all languages, we don't want it
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-light.css';
import 'vkbeautify';

// NOTE: Jake: 2019-04-15
// We must import all field types / fieldset at initial-load so that customised templates
// can use them.
import 'components/field';
import 'components/fieldset/fieldset';

import '~/inputmasks/input-masks-configuration';
import '~/validation/custom-validation-rules';

// NOTE(Jae): 2020-04-17 - WEBUI-2574
import '~/utils/string'; // deprecated, we want to remove this magic behaviour that adds to global scope

// NOTE(Jae): 2020-04-17 - WEBUI-2550 / WEBUI-2574
// This *must* be imported before other stylesheets AND other components in this project.
// This ensures these util classes are applied *before* other styling that "composes" them.
import '~/styles/util.module.scss';
import './../styles/main.scss';

import { GenericApi } from '~/api/generic-api';
import { initialState } from '~/app-store';
import { STORE_HISTORY_LIMIT } from '~/constants';
import { AuthInterceptor } from '~/utils/auth-interceptor';
import { FetchUtils } from '~/utils/fetch-utils';
import { Logger } from '~/utils/logger';

Bluebird.config({ warnings: false });

/** setup site config without starting so that test tools can utilize the same config file */
export function configurationWithoutStart(aurelia: Aurelia): void {
	const i18nNamespaces: string[] = ['common-strings'];

	aurelia.use
		.standardConfiguration()
		.plugin(PLATFORM.moduleName('aurelia-validation'))
		.plugin(PLATFORM.moduleName('aurelia-dialog'))
		.plugin(PLATFORM.moduleName('aurelia-store'), {
			initialState,
			history: {
				limit: STORE_HISTORY_LIMIT,
				undoable: true,
			},
		})
		.plugin(PLATFORM.moduleName('aurelia-i18n'), (i18n) => {
			const aliases = ['t', 'i18n'];
			TCustomAttribute.configureAliases(aliases);
			i18n.i18next.use(Backend);
			return i18n
				.setup({
					attributes: aliases,
					backend: { loadPath: './locales/{{lng}}/{{ns}}.json' },
					debug: false,
					defaultNS: i18nNamespaces[0],
					fallbackLng: 'en',
					lng: navigator.language.substring(0, 2),
					ns: i18nNamespaces,
				})
				.then(() => {
					const router = aurelia.container.get(AppRouter);
					router.transformTitle = (title) => i18n.tr(title);
				});
		})
		.plugin(PLATFORM.moduleName('aurelia-portal-attribute'));

	ValidationMessageProvider.prototype.getMessage = function (key) {
		const i18n: I18N = aurelia.container.get(I18N);
		let translationKey = `validation-errors.${key}`;
		if (!i18n.i18next.exists(translationKey)) {
			if (key in validationMessages) {
				translationKey = validationMessages[key];
			} else {
				translationKey = validationMessages.default;
			}
		}
		const translation = i18n.tr(translationKey);
		return this.parser.parse(translation);
	};

	FluentRuleCustomizer.prototype.withMessage = function (message) {
		const i18n: I18N = aurelia.container.get(I18N);
		this.rule.messageKey = 'custom';
		const translation = i18n.tr(message);
		this.rule.message = this.parsers.message.parse(translation);
		return this;
	};

	// Set locale for moment to browser locale.
	// Removing the webpackInclude comment results in all locales bundled respectively
	// If ContextReplacementPlugin is enable for moment locales, comment this dynamic import
	// and dynamically import specific locales, eg, `await import(/* webpackChunkName: "moment-locales/" */ 'moment/locale/de.js')`
	// await import(
	/* webpackInclude: /(en-.*|de*|en)\.js$/ */
	/* webpackChunkName: "moment-locales/" */
	// 'moment/locale/' + navigator.language.toLowerCase() + '.js');
	let momentLanguage;
	switch (navigator.language) {
		case 'en-US':
			momentLanguage = 'en';
			break;
		case 'en-gb':
			// fix for tree view as we cannot set ChromiumSwitch with "--lang=en-au"
			// so by default JxBrowser's navigator.language is set to 'en-gb'
			// we want moment.locale('en-au') here so that localised time format is set as h:mm A not HH:mm
			momentLanguage = 'en-au';
			break;
		default:
			momentLanguage = navigator.language;
	}
	const locale = moment.locale(momentLanguage);
	if (locale !== momentLanguage.toLocaleLowerCase()) {
		Logger.error(
			'Main',
			`Locale ${momentLanguage} is not supported, fall back to ${locale}`,
		);
	}

	// only enable xml and json
	hljs.registerLanguage('xml', require('highlight.js/lib/languages/xml'));
	hljs.registerLanguage('json', require('highlight.js/lib/languages/json'));

	// Setup Fetch
	const interceptor = aurelia.container.get(AuthInterceptor);
	FetchUtils.clear();
	FetchUtils.setBaseUrl(GenericApi.apiServerURL);
	FetchUtils.addInterceptor(interceptor);

	// Enable `developmentLogging` only on `DEVELOPMENT` mode
	if (DEVELOPMENT) {
		aurelia.use.developmentLogging();
	}
}

export async function configure(aurelia: Aurelia): Promise<void> {
	configurationWithoutStart(aurelia);
	await aurelia.start();
	await aurelia.setRoot(PLATFORM.moduleName('app'));
}
